import React, { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Upload, X, Image as ImageIcon } from "lucide-react";
import { api, imageUrl, formatINR } from "../../lib/api";
import { toast } from "sonner";

const empty = {
  slug: "", name: "", description: "", price: 0, compare_at_price: 0,
  images: [], categories: [], collections: [], material: "Anti-Tarnish Brass, 22kt Gold Plated",
  dimensions: "", care_instructions: "Store in the provided pouch. Wipe gently with a soft cotton cloth. Avoid direct contact with water and sweat.",
  stock: 25, is_bestseller: false, is_new: false, variants: [], tags: [],
};

const COLLECTION_OPTS = ["rajputana", "bridal", "antitarnish", "temple", "kundan", "polki", "festive", "bestsellers", "newarrivals", "under499", "under999"];
const CATEGORY_OPTS = ["necklaces", "earrings", "bangles", "rings"];

const toAbsoluteUrl = imageUrl;

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // product or empty
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/products", { withCredentials: true }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Delete this product?")) return;
    try { await api.delete(`/admin/products/${id}`, { withCredentials: true }); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-8 lg:p-12" data-testid="admin-products">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="label-tiny text-brand-gold">Catalog</p>
          <h1 className="font-serif-brand text-4xl text-brand-charcoal mt-1">Products · {products.length}</h1>
        </div>
        <button onClick={() => setEditing({ ...empty })} className="btn-primary flex items-center gap-2" data-testid="new-product-btn"><Plus size={14} /> New Product</button>
      </div>

      {loading ? <p className="text-brand-muted">Loading…</p> : (
        <div className="border border-brand-gold/25 bg-brand-ivory overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-brand-ivory-soft">
              <tr className="text-left label-tiny text-brand-muted">
                <th className="p-4">Image</th><th>Name</th><th>Price</th><th>Stock</th><th>Rating</th><th></th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-brand-gold/15" data-testid={`row-${p.slug}`}>
                  <td className="p-4"><img src={toAbsoluteUrl(p.images?.[0])} alt="" className="w-14 h-14 object-cover" /></td>
                  <td className="p-4"><p className="font-medium text-brand-charcoal">{p.name}</p><p className="text-xs text-brand-muted">{p.slug}</p></td>
                  <td className="p-4 text-brand-maroon font-medium">{formatINR(p.price)}</td>
                  <td className="p-4">{p.stock}</td>
                  <td className="p-4">★ {p.rating} ({p.review_count})</td>
                  <td className="p-4 text-right">
                    <button onClick={() => setEditing(p)} className="text-brand-charcoal hover:text-brand-maroon p-2" data-testid={`edit-${p.slug}`}><Edit2 size={14} /></button>
                    <button onClick={() => del(p.id)} className="text-brand-charcoal hover:text-brand-maroon p-2" data-testid={`del-${p.slug}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <ProductForm product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

const ProductForm = ({ product, onClose, onSaved }) => {
  const isNew = !product.id;
  const [form, setForm] = useState({ ...product });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (key, val) => setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/admin/upload", fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      set("images", [...(form.images || []), res.data.url]);
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };

  const removeImage = (idx) => set("images", form.images.filter((_, i) => i !== idx));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        compare_at_price: form.compare_at_price ? Number(form.compare_at_price) : null,
        stock: Number(form.stock) || 0,
        variants: Array.isArray(form.variants) ? form.variants : String(form.variants || "").split(",").map(v => v.trim()).filter(Boolean),
        tags: Array.isArray(form.tags) ? form.tags : String(form.tags || "").split(",").map(v => v.trim()).filter(Boolean),
      };
      if (isNew) {
        await api.post("/admin/products", payload, { withCredentials: true });
        toast.success("Product created");
      } else {
        await api.put(`/admin/products/${product.id}`, payload, { withCredentials: true });
        toast.success("Product updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-brand-charcoal/60 flex items-start justify-center overflow-y-auto p-4 lg:p-10" onClick={onClose} data-testid="product-form-modal">
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-brand-ivory w-full max-w-3xl p-6 lg:p-10 space-y-5">
        <div className="flex items-center justify-between">
          <div><p className="label-tiny text-brand-gold">{isNew ? "Create" : "Edit"}</p><h2 className="font-serif-brand text-3xl text-brand-charcoal mt-1">{isNew ? "New Product" : form.name}</h2></div>
          <button type="button" onClick={onClose} className="text-brand-charcoal p-2"><X size={20} /></button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <label className="block"><span className="label-tiny text-brand-muted">Slug</span>
            <input required disabled={!isNew} value={form.slug} onChange={(e) => set("slug", e.target.value)} className="w-full mt-1 border border-brand-gold/40 px-3 py-2 outline-none focus:border-brand-maroon disabled:bg-brand-ivory-soft" data-testid="form-slug" /></label>
          <label className="block"><span className="label-tiny text-brand-muted">Name</span>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full mt-1 border border-brand-gold/40 px-3 py-2 outline-none focus:border-brand-maroon" data-testid="form-name" /></label>
        </div>

        <label className="block"><span className="label-tiny text-brand-muted">Description</span>
          <textarea required rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="w-full mt-1 border border-brand-gold/40 px-3 py-2 outline-none focus:border-brand-maroon resize-none" data-testid="form-description" /></label>

        <div className="grid md:grid-cols-3 gap-4">
          <label className="block"><span className="label-tiny text-brand-muted">Price ₹</span>
            <input required type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="w-full mt-1 border border-brand-gold/40 px-3 py-2 outline-none focus:border-brand-maroon" data-testid="form-price" /></label>
          <label className="block"><span className="label-tiny text-brand-muted">Compare At ₹</span>
            <input type="number" value={form.compare_at_price || ""} onChange={(e) => set("compare_at_price", e.target.value)} className="w-full mt-1 border border-brand-gold/40 px-3 py-2 outline-none focus:border-brand-maroon" /></label>
          <label className="block"><span className="label-tiny text-brand-muted">Stock</span>
            <input required type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} className="w-full mt-1 border border-brand-gold/40 px-3 py-2 outline-none focus:border-brand-maroon" data-testid="form-stock" /></label>
        </div>

        <div>
          <p className="label-tiny text-brand-muted mb-2">Images</p>
          <div className="flex flex-wrap gap-3 mb-3">
            {(form.images || []).map((src, i) => (
              <div key={i} className="relative w-24 h-24 border border-brand-gold/30">
                <img src={toAbsoluteUrl(src)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(i)} className="absolute -top-2 -right-2 bg-brand-maroon text-brand-ivory rounded-full w-5 h-5 flex items-center justify-center"><X size={10} /></button>
              </div>
            ))}
            <label className="w-24 h-24 border-2 border-dashed border-brand-gold/50 flex flex-col items-center justify-center text-brand-muted hover:border-brand-maroon cursor-pointer">
              {uploading ? <Upload size={18} className="animate-pulse" /> : <ImageIcon size={18} />}
              <span className="text-[10px] mt-1">{uploading ? "Uploading" : "Upload"}</span>
              <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid="upload-image-input" />
            </label>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="label-tiny text-brand-muted mb-2">Categories</p>
            <div className="flex flex-wrap gap-2">
              {CATEGORY_OPTS.map((c) => (
                <button type="button" key={c} onClick={() => toggle("categories", c)} className={`px-3 py-1 text-xs border ${form.categories?.includes(c) ? "bg-brand-maroon text-brand-ivory border-brand-maroon" : "border-brand-gold/40 text-brand-charcoal"}`}>{c}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="label-tiny text-brand-muted mb-2">Collections</p>
            <div className="flex flex-wrap gap-2">
              {COLLECTION_OPTS.map((c) => (
                <button type="button" key={c} onClick={() => toggle("collections", c)} className={`px-3 py-1 text-xs border ${form.collections?.includes(c) ? "bg-brand-maroon text-brand-ivory border-brand-maroon" : "border-brand-gold/40 text-brand-charcoal"}`}>{c}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <label className="block"><span className="label-tiny text-brand-muted">Dimensions</span>
            <input value={form.dimensions} onChange={(e) => set("dimensions", e.target.value)} className="w-full mt-1 border border-brand-gold/40 px-3 py-2 outline-none focus:border-brand-maroon" /></label>
          <label className="block"><span className="label-tiny text-brand-muted">Variants (comma-separated)</span>
            <input value={Array.isArray(form.variants) ? form.variants.join(", ") : form.variants} onChange={(e) => set("variants", e.target.value)} className="w-full mt-1 border border-brand-gold/40 px-3 py-2 outline-none focus:border-brand-maroon" /></label>
        </div>

        <label className="block"><span className="label-tiny text-brand-muted">Material</span>
          <input value={form.material} onChange={(e) => set("material", e.target.value)} className="w-full mt-1 border border-brand-gold/40 px-3 py-2 outline-none focus:border-brand-maroon" /></label>

        <label className="block"><span className="label-tiny text-brand-muted">Care Instructions</span>
          <input value={form.care_instructions} onChange={(e) => set("care_instructions", e.target.value)} className="w-full mt-1 border border-brand-gold/40 px-3 py-2 outline-none focus:border-brand-maroon" /></label>

        <label className="block"><span className="label-tiny text-brand-muted">Tags (comma-separated)</span>
          <input value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => set("tags", e.target.value)} className="w-full mt-1 border border-brand-gold/40 px-3 py-2 outline-none focus:border-brand-maroon" /></label>

        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_bestseller} onChange={(e) => set("is_bestseller", e.target.checked)} /> Best Seller</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_new} onChange={(e) => set("is_new", e.target.checked)} /> New Arrival</label>
        </div>

        <div className="flex gap-3 pt-4">
          <button type="submit" disabled={saving} className="btn-primary" data-testid="save-product-btn">{saving ? "Saving…" : (isNew ? "Create Product" : "Save Changes")}</button>
          <button type="button" onClick={onClose} className="btn-outline-gold">Cancel</button>
        </div>
      </form>
    </div>
  );
};
